import type { Property } from '~/core/entities/property';
import type { Offer, RealEstateListing, WithContext } from 'schema-dts';

import { PropertyJsonLdPresenter } from '~/presenters/PropertyJsonLdPresenter';
import { joinURL } from 'ufo';

export function usePropertiesListingJsonLd(
	propertiesGetter: () => Property[],
	{
		pageTitle: pageTitleGetter,
		pageDescription: pageDescriptionGetter,
		transactionType: transactionTypeGetter
	}: {
		pageTitle: () => string;
		pageDescription: () => string;
		transactionType: () => 'sell' | 'rent' | undefined;
	}
) {
	const websiteUrl = useRuntimeConfig().public.ORIGIN;
	const route = useRoute();

	const properties = computed<Property[]>(propertiesGetter);
	const pageTitle = computed<string>(pageTitleGetter);
	const pageDescription = computed<string>(pageDescriptionGetter);
	const transactionType = computed(transactionTypeGetter);

	const realEstateListingJsonLd = computed<WithContext<RealEstateListing>>(
		() => {
			const propertyOffers = properties.value.flatMap(property =>
				PropertyJsonLdPresenter.getPropertyOffersJsonLd(property, {
					websiteUrl,
					transactionType: transactionType.value
				}).map<Offer>(({ itemOffered: _itemOffered, ...offer }) => ({
					...offer,
					name: property.ad.title
				}))
			);

			const prices = propertyOffers.map(offer => Number(offer.price));

			const listing: WithContext<RealEstateListing> = {
				'@type': 'RealEstateListing',
				'@context': 'https://schema.org',
				url: joinURL(websiteUrl, route.fullPath),
				name: pageTitle.value,
				description: pageDescription.value
			};

			if (propertyOffers.length === 0) return listing;

			return {
				...listing,
				offers: {
					'@type': 'AggregateOffer',
					priceCurrency: 'BRL',
					lowPrice: Math.min(...prices),
					highPrice: Math.max(...prices),
					offerCount: propertyOffers.length,
					offers: propertyOffers
				}
			};
		}
	);

	useJsonld(() => realEstateListingJsonLd.value);
}
